import { type ReactNode } from 'react';

import { cn } from '@apps/web/lib/utils';

export type ProfileSection = {
    readonly key: string;
    readonly label: string;
    readonly count?: number;
    readonly content: ReactNode;
};

type ProfileShellProps = {
    readonly header: ReactNode;
    readonly sections: ReadonlyArray<ProfileSection>;
    readonly activeSectionKey: string;
    readonly onSectionChange: (key: string) => void;
    // Extension surface (~420px): nav renders as a horizontal chip row
    // above the content instead of a left rail.
    readonly stacked?: boolean;
};

/**
 * Shared skeleton for the client/caregiver profile popups: header slot on
 * top, section nav (left rail or stacked chips), and the active section's
 * content. Controlled: the parent owns the active-section state so it can
 * map deep-link props (e.g. initialTab) onto section keys.
 */
export function ProfileShell({
    header,
    sections,
    activeSectionKey,
    onSectionChange,
    stacked = false,
}: ProfileShellProps) {
    const activeSection =
        sections.find((section) => section.key === activeSectionKey) ??
        sections[0];

    const navButtons = sections.map((section) => {
        const isActive = section.key === activeSection?.key;
        return (
            <button
                key={section.key}
                type="button"
                aria-current={isActive ? 'page' : undefined}
                onClick={() => onSectionChange(section.key)}
                className={cn(
                    'focus-visible:ring-ring flex min-h-9 shrink-0 items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
                    stacked
                        ? 'whitespace-nowrap'
                        : 'whitespace-nowrap md:w-full',
                    isActive
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
            >
                <span className={cn(!stacked && 'flex-1')}>
                    {section.label}
                </span>
                {section.count !== undefined && (
                    <span className="text-muted-foreground/70 text-xs">
                        {section.count}
                    </span>
                )}
            </button>
        );
    });

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {header}
            {stacked ? (
                <div className="flex min-h-0 flex-1 flex-col">
                    <nav
                        data-testid="profile-nav-stacked"
                        className="flex shrink-0 gap-1 overflow-x-auto border-b px-2 py-2"
                    >
                        {navButtons}
                    </nav>
                    <div
                        data-testid="profile-section-content"
                        className="min-h-0 flex-1 overflow-y-auto p-4"
                    >
                        {activeSection?.content}
                    </div>
                </div>
            ) : (
                <div className="flex min-h-0 flex-1 flex-col md:flex-row">
                    <nav
                        data-testid="profile-nav"
                        className="bg-muted/30 flex shrink-0 gap-1 overflow-x-auto border-b px-2 py-2 md:w-[168px] md:flex-col md:overflow-x-visible md:overflow-y-auto md:border-r md:border-b-0 md:p-3"
                    >
                        {navButtons}
                    </nav>
                    <div
                        data-testid="profile-section-content"
                        className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5"
                    >
                        {activeSection?.content}
                    </div>
                </div>
            )}
        </div>
    );
}
